/**
 * The middle row of the summary dashboard: by-edition, by-field-size, and a
 * daily trend. Simple CSS bars (matching the mockup); recharts is available if
 * we later want proper axes.
 */

const pctText = (n) => `${(n ?? 0).toFixed(1)}%`;
const clamp = (n) => Math.max(0, Math.min(100, n ?? 0));

function BarRow({ label, n, rate }) {
  return (
    <div>
      <div className="flex justify-between mb-1">
        <span>
          {label} <span className="text-slate-400">({n})</span>
        </span>
        <span className="font-semibold">{pctText(rate)}</span>
      </div>
      <div className="h-2 bg-slate-100 rounded">
        <div
          className="h-2 bg-emerald-500 rounded"
          style={{ width: `${clamp(rate)}%` }}
        />
      </div>
    </div>
  );
}

function Panel({ title, children, footnote }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
      <div className="text-xs font-semibold text-slate-500 uppercase mb-3">
        {title}
      </div>
      {children}
      {footnote && (
        <div className="text-[11px] text-slate-400 mt-3">{footnote}</div>
      )}
    </div>
  );
}

export default function SummaryPanels({ summary }) {
  const { byEdition, byFieldSize, trend } = summary;
  const maxTrend = Math.max(1, ...trend.map((t) => t.rate));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
      <Panel title="By tip edition">
        <div className="space-y-3 text-sm">
          {byEdition.map((e) => (
            <BarRow key={e.key} label={e.label} n={e.legs} rate={e.rate} />
          ))}
        </div>
      </Panel>

      <Panel title="By field size">
        <div className="space-y-3 text-sm">
          {byFieldSize.map((b) => (
            <BarRow
              key={b.key}
              label={`${b.label} (${b.places})`}
              n={b.legs}
              rate={b.rate}
            />
          ))}
        </div>
      </Panel>

      <Panel title={`Trend (${trend.length} days)`}>
        {trend.length ? (
          <>
            <div className="flex items-end gap-1.5 h-28">
              {trend.map((t) => (
                <div
                  key={t.date}
                  title={`${t.date}: ${pctText(t.rate)} · ${t.n} legs`}
                  className="flex-1 bg-emerald-500/80 rounded-t"
                  style={{
                    height: `${Math.max(4, (t.rate / maxTrend) * 100)}%`,
                  }}
                />
              ))}
            </div>
            <div className="flex justify-between text-[10px] text-slate-400 mt-1">
              <span>{trend[0]?.date.slice(5)}</span>
              <span>{trend[trend.length - 1]?.date.slice(5)}</span>
            </div>
          </>
        ) : (
          <div className="text-sm text-slate-400 py-8 text-center">
            No races in range
          </div>
        )}
      </Panel>
    </div>
  );
}
